import React, { useState, useEffect } from 'react';
import './session-summary-popup.scss';
import { FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { saveSessionSummary } from '../../services/sessionHistoryService';

interface SessionSummaryPopupProps {
  specialtyName: string;
  topicName: string;
  onClose: () => void;
  isOpen: boolean;
  automatedSummary?: {
    ipsScore: number;
    dataGatheringScore: number;
    diagnosisMgtScore: number;
    areasForImprovement: string;
  } | null;
}

interface SessionSummary {
  id: string;
  specialtyName: string;
  topicName: string;
  ipsScore: number | null;
  dataGatheringScore: number | null;
  diagnosisMgtScore: number | null;
  areasForImprovement: string;
  timestamp: string;
}

const PASS_THRESHOLD = 9;

export const SessionSummaryPopup: React.FC<SessionSummaryPopupProps> = ({
  specialtyName,
  topicName,
  onClose,
  isOpen,
  automatedSummary,
}) => {
  const [ipsScore, setIpsScore] = useState<number | null>(null);
  const [dataGatheringScore, setDataGatheringScore] = useState<number | null>(null);
  const [diagnosisMgtScore, setDiagnosisMgtScore] = useState<number | null>(null);
  const [areasForImprovement, setAreasForImprovement] = useState<string>('');
  const [saved, setSaved] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) {
      setIpsScore(null);
      setDataGatheringScore(null);
      setDiagnosisMgtScore(null);
      setAreasForImprovement('');
      setSaved(false);
    } else if (automatedSummary) {
      setIpsScore(automatedSummary.ipsScore);
      setDataGatheringScore(automatedSummary.dataGatheringScore);
      setDiagnosisMgtScore(automatedSummary.diagnosisMgtScore);
      setAreasForImprovement(automatedSummary.areasForImprovement);
    }
  }, [isOpen, automatedSummary]);

  const totalScore =
    (ipsScore ?? 0) + (dataGatheringScore ?? 0) + (diagnosisMgtScore ?? 0);
  const isPassing = totalScore >= PASS_THRESHOLD;

  const handleSave = async () => {
    const summary: SessionSummary = {
      id: `${Date.now()}`,
      specialtyName,
      topicName,
      ipsScore,
      dataGatheringScore,
      diagnosisMgtScore,
      areasForImprovement,
      timestamp: new Date().toISOString(),
    };

    // Always save to localStorage
    const existingSummaries = JSON.parse(
      localStorage.getItem('sessionSummaries') || '[]'
    );
    existingSummaries.push(summary);
    localStorage.setItem('sessionSummaries', JSON.stringify(existingSummaries));

    // Also save to Supabase for logged-in users
    if (user?.id) {
      await saveSessionSummary(user.id, {
        specialtyName,
        topicName,
        ipsScore,
        dataGatheringScore,
        diagnosisMgtScore,
        areasForImprovement,
      });
    }

    setSaved(true);

    // Auto-close after a moment and navigate home
    setTimeout(() => {
      onClose();
      window.location.href = '/';
    }, 1200);
  };

  if (!isOpen) return null;

  // New: Loading State
  if (!automatedSummary) {
    return (
      <div className="session-summary-popup-overlay">
        <div className="session-summary-popup loading">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>Performance Analysis in Progress</h3>
            <p>Gathering final metrics from your session...</p>
          </div>
        </div>
      </div>
    );
  }

  const scorePercent = Math.round((totalScore / 12) * 100);

  return (
    <div className="session-summary-popup-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="session-summary-popup">
        {/* Close button */}
        <button className="popup-close" onClick={onClose}>
          <FaTimes />
        </button>

        {/* Score Ring & Badge */}
        <div className="score-hero">
          <div className="score-ring">
            <svg viewBox="0 0 120 120">
              <circle
                cx="60" cy="60" r="52"
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
              />
              <circle
                cx="60" cy="60" r="52"
                fill="none"
                stroke={isPassing ? '#10b981' : '#ef4444'}
                strokeWidth="8"
                strokeDasharray={`${scorePercent * 3.27} 327`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                className="score-ring-progress"
              />
            </svg>
            <div className="score-ring-text">
              <span className="score-number">{totalScore}</span>
              <span className="score-total">/12</span>
            </div>
          </div>

          <div className={`pass-fail-badge ${isPassing ? 'pass' : 'fail'}`}>
            {isPassing ? (
              <><FaCheckCircle /> PASS</>
            ) : (
              <><FaTimesCircle /> FAIL</>
            )}
          </div>

          <div className="case-info">
            <span className="specialty-tag">{specialtyName}</span>
            <span className="topic-name">{topicName}</span>
          </div>
        </div>

        {/* Score Pills */}
        <div className="score-pills">
          <div className="score-pill">
            <label>IPS</label>
            <div className="pill-input-group">
              <input
                type="number"
                min="0"
                max="4"
                value={ipsScore === null ? '' : ipsScore}
                onChange={(e) => setIpsScore(Number(e.target.value))}
              />
              <span className="pill-max">/4</span>
            </div>
          </div>
          <div className="score-pill">
            <label>Data Gathering</label>
            <div className="pill-input-group">
              <input
                type="number"
                min="0"
                max="4"
                value={dataGatheringScore === null ? '' : dataGatheringScore}
                onChange={(e) => setDataGatheringScore(Number(e.target.value))}
              />
              <span className="pill-max">/4</span>
            </div>
          </div>
          <div className="score-pill">
            <label>Diagnosis & Mgt</label>
            <div className="pill-input-group">
              <input
                type="number"
                min="0"
                max="4"
                value={diagnosisMgtScore === null ? '' : diagnosisMgtScore}
                onChange={(e) => setDiagnosisMgtScore(Number(e.target.value))}
              />
              <span className="pill-max">/4</span>
            </div>
          </div>
        </div>

        {/* Improvement Notes */}
        <div className="improvement-section">
          <label>Areas for Improvement</label>
          <textarea
            value={areasForImprovement}
            onChange={(e) => setAreasForImprovement(e.target.value)}
            placeholder="Key areas to work on..."
            rows={3}
          />
        </div>

        {/* Save */}
        <button
          className={`save-button ${saved ? 'saved' : ''}`}
          onClick={handleSave}
          disabled={saved}
        >
          {saved ? '✓ Saved!' : 'Save Summary'}
        </button>
      </div>
    </div>
  );
};
